/**
 * AuthContext.js — Session management and role-based permission flags
 *
 * Wraps the app with a React Context that exposes:
 *   currentUser  – user object (id, name, email, role_level, avatar) or null
 *   login()      – authenticate + populate currentUser
 *   logout()     – invalidate session + clear currentUser
 *   loading      – true while the initial session check is running
 *   isManager    – true if role_level ≤ 1 (Admin or Manager)
 *   isAdmin      – true if role_level === 0
 *
 * Storage strategy:
 *   The user object is cached in localStorage ('currentUser') so the UI can
 *   render immediately on page load without waiting for a network round-trip.
 *   On every mount the session is re-validated against /users/me — if the
 *   session has expired (cookie in prod, access token in dev) Directus returns
 *   401 and we clear the stale cache.
 *
 * Auth mode is environment-split (see directus.js):
 *   Production  — httpOnly session cookie, no tokens in JS
 *   Development — Bearer tokens stored in localStorage as 'dev_access_token' /
 *                 'dev_refresh_token'; directus.js handles refresh transparently
 *
 * Session keep-alive strategy (three layers):
 *   1. Mount / cold-open: validates the session on every app open. If the
 *      session hasn't been refreshed in over 1 hour, proactively refreshes it
 *      so the TTL is reset before it ages dangerously. This is the primary
 *      keep-alive for supervisors who open the app briefly each day.
 *   2. Periodic timer: refreshes every 23 hours while a tab is open, covering
 *      devices that run the app continuously (e.g. floor kiosk).
 *   3. Visibility handler: refreshes when the tab becomes visible after being
 *      idle for >5 minutes, covering device wake-ups and task-switches.
 *
 *   The last-refresh timestamp is persisted to localStorage (LAST_REFRESH_KEY)
 *   so browser restarts don't reset the counter. Previously it was held only
 *   in a useRef, which meant cold opens never triggered a refresh even when
 *   the session had been sitting idle for days.
 *
 * Error handling — auth errors vs. network errors:
 *   A Directus auth rejection (401 with an errors[] payload) means the server
 *   has explicitly invalidated the session → the user must log in again.
 *   A network failure (TypeError, no connectivity) does NOT mean the session
 *   is dead; we keep the cached user so the app remains usable while offline
 *   and retries on the next request when connectivity returns.
 *   isAuthError() is the single gate for this distinction.
 *
 * Role levels (from Directus role.level custom field):
 *   0 = Admin     — all actions, including Reset attendance
 *   1 = Manager   — approve checkout, record wages, pay advance
 *   2+ = Supervisor — start/end shift, break, mark absent only
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { login as apiLogin, logout as apiLogout, getCurrentUser, refreshSession } from '../api/directus';

const AuthContext = createContext(null);

// Persists the epoch ms of the last successful session refresh across browser
// restarts. Previously this lived only in a useRef (reset to Date.now() on
// every mount), causing the keep-alive to miss refreshes during short sessions.
const LAST_REFRESH_KEY = 'session_last_refresh';

/**
 * Returns true only when the error is a server-side auth rejection — i.e.
 * a Directus error payload with a non-empty errors[] array. Network failures
 * (TypeError thrown by fetch) return false, allowing callers to keep the user
 * logged in and retry when connectivity is restored.
 *
 * @param {unknown} err
 * @returns {boolean}
 */
function isAuthError(err) {
  return Array.isArray(err?.errors) && err.errors.length > 0;
}

export function AuthProvider({ children }) {
  // Hydrate synchronously from localStorage so the UI doesn't flash "loading"
  // on every page refresh when a valid session already exists.
  const [currentUser, setCurrentUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('currentUser')); } catch { return null; }
  });
  const [loading, setLoading] = useState(true);

  // Initialised from localStorage so the timestamp survives browser restarts.
  // A value of 0 means "never refreshed" and guarantees a refresh on first open.
  const lastRefreshRef = useRef(
    parseInt(localStorage.getItem(LAST_REFRESH_KEY) || '0', 10)
  );

  /** Stamp the last-refresh time in both the ref and localStorage. */
  const recordRefresh = useCallback(() => {
    const now = Date.now();
    lastRefreshRef.current = now;
    localStorage.setItem(LAST_REFRESH_KEY, String(now));
  }, []);

  /**
   * Fetch fresh user metadata from /users/me and persist to localStorage.
   * Called after login and on every app mount to validate the session cookie.
   * Returns the normalised user object so callers can await it directly.
   */
  const saveCurrentUser = useCallback(async () => {
    const userMeta = await getCurrentUser();
    const BASE = process.env.REACT_APP_DIRECTUS_URL || 'https://staff.innomalous.com';
    const user = {
      id: userMeta.data.id,
      first_name: userMeta.data.first_name,
      last_name: userMeta.data.last_name,
      full_name: `${userMeta.data.first_name} ${userMeta.data.last_name}`.trim(),
      email: userMeta.data.email,
      // Fallback to 99 (no permissions) if role.level is missing
      role_level: userMeta.data.role?.level ?? 99,
      role_name: userMeta.data.role?.name ?? null,
      avatar: userMeta.data.avatar
        ? `${BASE}/assets/${userMeta.data.avatar.filename_disk}`
        : null,
    };
    localStorage.setItem('currentUser', JSON.stringify(user));
    setCurrentUser(user);
    return user;
  }, []);

  /** Authenticate, then immediately fetch and store user metadata. */
  const login = useCallback(async (email, password) => {
    await apiLogin(email, password);
    await saveCurrentUser();
    recordRefresh();
  }, [saveCurrentUser, recordRefresh]);

  /**
   * Invalidate the server session and clear all local state.
   * The try/catch swallows 401 errors that occur when the cookie already
   * expired — we want to clear local state regardless.
   */
  const logout = useCallback(async () => {
    try { await apiLogout(); } catch {}
    localStorage.removeItem('currentUser');
    localStorage.removeItem(LAST_REFRESH_KEY);
    setCurrentUser(null);
  }, []);

  /**
   * Proactive session keep-alive.
   *
   * Three triggers:
   *   - setInterval every 23 hours (long-running tabs / kiosk use)
   *   - visibilitychange after >5 minutes idle (device wake, tab switch)
   *   - Mount check below (cold open after browser restart)
   *
   * On failure, only clears the session for explicit server auth rejections.
   * Network errors (offline, timeout) are silently ignored — the session is
   * still alive server-side and will succeed once connectivity returns.
   */
  useEffect(() => {
    if (!currentUser) return;

    async function doRefresh() {
      try {
        await refreshSession();
        recordRefresh();
      } catch (err) {
        if (isAuthError(err)) {
          // Server explicitly rejected the session — must re-authenticate
          localStorage.removeItem('currentUser');
          localStorage.removeItem(LAST_REFRESH_KEY);
          setCurrentUser(null);
        }
        // Network errors are silently dropped — session is still valid
        // server-side; the next connectivity window will succeed.
      }
    }

    const TWENTY_THREE_HOURS = 23 * 60 * 60 * 1000;
    const intervalId = setInterval(doRefresh, TWENTY_THREE_HOURS);

    // Refresh when the app returns from background after >5 minutes idle
    function handleVisibility() {
      if (
        document.visibilityState === 'visible' &&
        Date.now() - lastRefreshRef.current > 5 * 60 * 1000
      ) {
        doRefresh();
      }
    }
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [currentUser?.id, recordRefresh]); // eslint-disable-line

  /**
   * Session validation on mount (cold-open keep-alive).
   *
   * Steps:
   *   1. If no cached user → nothing to validate, show login.
   *   2. Migration guard: clear stale token-based entries from before session mode.
   *   3. Validate the session with /users/me. directus.js auto-refreshes on 401,
   *      so a recently-expired session is transparently recovered here.
   *   4. Proactive refresh: if the session hasn't been refreshed in over 1 hour
   *      (persisted across browser restarts), renew it now. This is the key step
   *      that prevents slow expiry for supervisors who open the app briefly each
   *      day — without it, the session aged silently toward the 7-day TTL.
   *   5. Error handling: only clear the session for explicit auth rejections.
   *      Network failures keep the cached user so the app stays usable offline.
   *
   * The eslint-disable suppresses exhaustive-deps for the empty dependency array.
   * This effect must run exactly once on mount — not on every render.
   */
  useEffect(() => {
    async function check() {
      const user = currentUser;
      if (!user) { setLoading(false); return; }

      // Migrate: clear old token-based localStorage entries from before session mode
      if (user.access_token) {
        localStorage.removeItem('currentUser');
        setCurrentUser(null);
        setLoading(false);
        return;
      }

      try {
        // Validate — directus.js auto-refreshes once on 401 before throwing
        await saveCurrentUser();

        // Proactively refresh if the session hasn't been renewed in over 1 hour.
        // Covers browser restarts and short daily sessions where the 23h interval
        // never fires and the visibility handler alone isn't sufficient.
        if (Date.now() - lastRefreshRef.current > 60 * 60 * 1000) {
          try {
            await refreshSession();
            recordRefresh();
          } catch {
            // Non-fatal: session was just confirmed valid above. The next
            // interval / visibility event will retry.
          }
        }
      } catch (err) {
        // Only log out for server auth rejections (errors[] present).
        // Network errors keep the cached user — the app works offline and
        // the next API call will succeed when connectivity returns.
        if (isAuthError(err)) {
          localStorage.removeItem('currentUser');
          localStorage.removeItem(LAST_REFRESH_KEY);
          setCurrentUser(null);
        }
      }

      setLoading(false);
    }
    check();
  }, []); // eslint-disable-line

  // Derived permission flags — components import these instead of comparing
  // role_level directly so the threshold logic lives in one place.
  const isManager = currentUser?.role_level <= 1;
  const isAdmin = currentUser?.role_level === 0;

  return (
    <AuthContext.Provider value={{ currentUser, login, logout, loading, isManager, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Convenience hook — use inside any component that needs auth state or actions.
 *
 * @returns {{ currentUser, login, logout, loading, isManager, isAdmin }}
 */
export function useAuth() {
  return useContext(AuthContext);
}
